import React, { useState, useEffect } from 'react';
import { Upload, Trash2, RefreshCw, FileText, CheckCircle, AlertCircle, Send, Users } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { PageHeader } from '../components/FilterBar';
import Badge from '../components/Badge';
import SearchableSelect from '../components/SearchableSelect';

const POLICY_TYPES = [
  { value: 'handbook', label: 'CL Synergy Group IT - Policy Handbook', desc: 'Company-wide IT policy handbook — included in all handover emails and visible to all users in My Policies' },
  { value: 'general', label: 'CL Synergy IT Quick Reference Guide', desc: 'IT quick reference guide — included in all handover emails and visible to all users in My Policies' },
];

export default function Policies() {
  const { isIT, isAdmin } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [acceptances, setAcceptances] = useState([]);
  const [ackRecords, setAckRecords] = useState([]);
  const [neverRequested, setNeverRequested] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [tab, setTab] = useState('policies');

  // Acknowledgement send state
  const [ackMode, setAckMode] = useState('company'); // 'company' | 'role' | 'users'
  const [ackCompany, setAckCompany] = useState('');
  const [ackDept, setAckDept] = useState('');
  const [ackRoles, setAckRoles] = useState([]);
  const [ackUsers, setAckUsers] = useState([]); // selected system_user ids for 'users' mode
  const [allUsers, setAllUsers] = useState([]); // full user list for 'users' mode
  const [userSearch, setUserSearch] = useState('');
  const [ackPreview, setAckPreview] = useState([]);
  const [ackPreviewLoading, setAckPreviewLoading] = useState(false);
  const [ackSending, setAckSending] = useState(false);
  const [ackResult, setAckResult] = useState(null);
  const [ackError, setAckError] = useState('');

  const ALL_ROLES = [
    { value: 'general_user', label: 'General Users' },
    { value: 'it_staff', label: 'IT Staff' },
    { value: 'junior_it', label: 'Junior IT' },
    { value: 'director', label: 'Directors' },
    { value: 'finance_staff', label: 'Finance Staff' },
  ];

  // Acceptance records filter
  const [ackFilterStatus, setAckFilterStatus] = useState('pending');
  const [ackFilterPolicy, setAckFilterPolicy] = useState('');
  const [ackFilterCompany, setAckFilterCompany] = useState('');
  const [resendingId, setResendingId] = useState(null); // system_user_id being resent
  const [resendResults, setResendResults] = useState({}); // { [system_user_id]: 'ok'|'err' }
  const [resendAllLoading, setResendAllLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        api.get('/policies').then(r => r.data),
        api.get('/policies/acceptances').then(r => r.data).catch(() => [])
      ]);
      setPolicies(p); setAcceptances(a);
    } catch {} finally { setLoading(false); }
  };

  const loadAckRecords = async () => {
    try {
      const params = {};
      // Never filter by status — always load both pending and acknowledged for two-section display
      if (ackFilterPolicy) params.policy_id = ackFilterPolicy;
      if (ackFilterCompany) params.company_id = ackFilterCompany;
      const r = await api.get('/policies/all-acknowledgements', { params });
      setAckRecords(r.data || []);
      const nr = await api.get('/policies/never-requested').catch(() => null);
      if (nr) setNeverRequested(nr.data || []);
    } catch {}
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get('/employees/meta/companies').then(r => setCompanies(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (tab === 'acknowledgements') loadAckRecords();
  }, [tab]); // eslint-disable-line

  const handleUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(type);
    try {
      const fd = new FormData();
      fd.append('policy', file);
      fd.append('name', POLICY_TYPES.find(t => t.value === type)?.label || type);
      fd.append('type', type);
      await api.post('/policies/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch (err) { alert(err.response?.data?.error || 'Upload failed'); }
    finally { setUploading(null); e.target.value = ''; }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this policy document?')) return;
    await api.delete(`/policies/${id}`);
    load();
  };

  const getPolicy = (type) => policies.find(p => p.type === type);

  const handleSendAck = async () => {
    setAckSending(true); setAckError(''); setAckResult(null);
    try {
      let body;
      if (ackMode === 'role') body = { target_roles: ackRoles.join(',') };
      else if (ackMode === 'users') body = { user_ids: ackUsers.join(',') };
      else body = { company_id: ackCompany || null, department_id: ackDept || null };
      const r = await api.post('/policies/send-acknowledgement', body);
      setAckResult(r.data);
      setAckPreview([]);
    } catch (err) { setAckError(err.response?.data?.error || 'Failed to send'); }
    finally { setAckSending(false); }
  };

  const handleResend = async (systemUserId) => {
    setResendingId(systemUserId);
    try {
      await api.post('/policies/resend-ack', { system_user_id: systemUserId });
      setResendResults(prev => ({ ...prev, [systemUserId]: 'ok' }));
    } catch (err) {
      setResendResults(prev => ({ ...prev, [systemUserId]: 'err' }));
    } finally { setResendingId(null); }
  };

  const handleResendAll = async () => {
    // Get unique system_user_ids from pending records that have an id
    const pendingUserIds = [...new Set(
      ackRecords.filter(r => r.status === 'pending' && r.system_user_id).map(r => r.system_user_id)
    )];
    if (pendingUserIds.length === 0) return;
    setResendAllLoading(true);
    for (const uid of pendingUserIds) {
      try {
        await api.post('/policies/resend-ack', { system_user_id: uid });
        setResendResults(prev => ({ ...prev, [uid]: 'ok' }));
      } catch {
        setResendResults(prev => ({ ...prev, [uid]: 'err' }));
      }
    }
    setResendAllLoading(false);
  };

  const TABS = [
    ['policies', 'Policy Documents'],
    ['send-ack', 'Send for Acknowledgement'],
    ['acknowledgements', 'Acceptance Records'],
    ['handovers', 'Handover Acceptances'],
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="IT Policies">
        <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13} className={loading ? 'animate-spin' : ''}/></button>
      </PageHeader>

      <div className="text-xs px-1" style={{ color: '#334155' }}>
        Upload policy documents. Employees are required to review and acknowledge these policies when they first log in or when requested by IT Administration.
      </div>

      {/* Tab bar */}
      <div className="overflow-x-auto pb-1" style={{scrollbarWidth:'thin',scrollbarColor:'rgba(255,255,255,0.1) transparent'}}>
        <div className="flex gap-1 p-1 rounded-xl" style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.06)',width:'max-content',minWidth:'100%'}}>
          {TABS.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap"
              style={{ background: tab===key?'rgba(59,130,246,0.2)':'transparent', color: tab===key?'#60a5fa':'#475569', border: tab===key?'1px solid rgba(59,130,246,0.3)':'1px solid transparent' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── POLICY DOCUMENTS TAB ─────────────────────────────── */}
      {tab === 'policies' && (
        <div className="space-y-4">
          {POLICY_TYPES.map(pt => {
            const existing = getPolicy(pt.value);
            return (
              <div key={pt.value} className="glass glow-border rounded-2xl p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: existing ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.05)', border: `1px solid ${existing ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                      {existing ? <CheckCircle size={18} style={{ color: '#10b981' }}/> : <FileText size={18} style={{ color: '#334155' }}/>}
                    </div>
                    <div>
                      <div className="font-semibold text-sm" style={{ color: '#e2e8f0' }}>{pt.label}</div>
                      <div className="text-xs mt-0.5" style={{ color: '#475569' }}>{pt.desc}</div>
                      {existing && (
                        <div className="flex items-center gap-2 mt-2">
                          <a href={`/api/policies/view/${existing.id}?token=${localStorage.getItem('token')}`} target="_blank" rel="noreferrer" className="text-xs" style={{ color: '#60a5fa' }}>
                            📄 {existing.file_name}
                          </a>
                          <span className="text-xs" style={{ color: '#334155' }}>
                            Uploaded {new Date(existing.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isIT && (
                      <label className="btn-primary text-xs cursor-pointer flex items-center gap-1.5">
                        <Upload size={12}/>
                        {uploading === pt.value ? 'Uploading...' : existing ? 'Replace' : 'Upload'}
                        <input type="file" className="hidden" accept=".pdf,.doc,.docx"
                          disabled={uploading === pt.value}
                          onChange={e => handleUpload(e, pt.value)}/>
                      </label>
                    )}
                    {isAdmin && existing && (
                      <button onClick={() => handleDelete(existing.id)} className="btn-ghost px-2 py-1 text-xs" style={{ color: '#ef4444' }}>
                        <Trash2 size={11}/>
                      </button>
                    )}
                  </div>
                </div>
                {!existing && (
                  <div className="mt-3 px-3 py-2 rounded-xl text-xs" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', color: '#fbbf24' }}>
                    ⚠ No document uploaded yet
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── SEND FOR ACKNOWLEDGEMENT TAB ────────────────────── */}
      {tab === 'send-ack' && (
        <div className="space-y-4 max-w-2xl">
          <div className="glass glow-border rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Send size={15} style={{color:'#60a5fa'}}/>
              <span className="font-semibold text-sm" style={{color:'#e2e8f0'}}>Send Policy Acknowledgement Request</span>
            </div>
            {/* Target Mode Toggle */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {[['company','By Company / Dept'],['role','By User Role'],['users','By Specific Users']].map(([m,l]) => (
                <button key={m} type="button" onClick={() => {
                  setAckMode(m); setAckPreview([]); setAckResult(null); setAckError('');
                  if (m === 'users' && allUsers.length === 0) {
                    api.get('/settings/users-by-role', { params: { roles: 'admin,it_staff,junior_it,director,finance_staff,general_user' } })
                      .then(r => setAllUsers(r.data || [])).catch(() => {});
                  }
                }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={{ background: ackMode===m?'rgba(59,130,246,0.2)':'rgba(255,255,255,0.03)', color: ackMode===m?'#60a5fa':'#475569', border: ackMode===m?'1px solid rgba(59,130,246,0.3)':'1px solid rgba(255,255,255,0.06)' }}>
                  {l}
                </button>
              ))}
            </div>
            <div className="text-xs mb-4" style={{color:'#475569'}}>
              Users will receive an email notification and see a pop-up acknowledgement on their next login. Users who have already acknowledged will not receive duplicate requests.
            </div>

            <div className="space-y-4">
              {ackMode === 'company' ? (
                <>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Company</label>
                    <SearchableSelect value={ackCompany} onChange={v => {
                      setAckCompany(v); setAckDept(''); setAckPreview([]); setAckResult(null);
                      if (v) {
                        api.get('/employees/meta/departments', { params: { company_id: v } })
                          .then(r => setDepts(r.data || [])).catch(() => {});
                      } else { setDepts([]); }
                    }}
                      options={companies.map(c => ({ value: c.id, label: c.name, hint: c.code }))}
                      placeholder="All Companies"/>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Department</label>
                    <select value={ackDept} onChange={e => { setAckDept(e.target.value); setAckPreview([]); setAckResult(null); }} className="input-dark" disabled={!ackCompany}>
                      <option value="">All Departments{ackCompany ? '' : ' (select company first)'}</option>
                      {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <button type="button" onClick={async () => {
                    setAckPreviewLoading(true); setAckPreview([]);
                    try {
                      const params = { limit: 200, status: 'active' };
                      if (ackCompany) params.company_id = ackCompany;
                      if (ackDept) params.department_id = ackDept;
                      const r = await api.get('/employees', { params });
                      setAckPreview(r.data.data || []);
                    } catch {} finally { setAckPreviewLoading(false); }
                  }} className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
                    <Users size={11}/>{ackPreviewLoading ? 'Loading...' : 'Preview Recipients'}
                  </button>
                </>
              ) : ackMode === 'role' ? (
                <>
                  <div>
                    <label className="block text-xs font-medium mb-2" style={{color:'#475569'}}>Select User Roles to target</label>
                    <div className="space-y-2">
                      {ALL_ROLES.map(role => (
                        <label key={role.value} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={ackRoles.includes(role.value)}
                            onChange={e => { setAckRoles(prev => e.target.checked ? [...prev, role.value] : prev.filter(r => r !== role.value)); setAckPreview([]); }}
                            style={{accentColor:'#818cf8'}}/>
                          <span className="text-xs" style={{color:'#e2e8f0'}}>{role.label}</span>
                        </label>
                      ))}
                    </div>
                    {ackRoles.length === 0 && <div className="mt-2 text-xs" style={{color:'#f59e0b'}}>Select at least one role.</div>}
                  </div>
                  <button type="button" onClick={async () => {
                    if (ackRoles.length === 0) return;
                    setAckPreviewLoading(true); setAckPreview([]);
                    try {
                      const r = await api.get('/settings/users-by-role', { params: { roles: ackRoles.join(',') } });
                      setAckPreview(r.data || []);
                    } catch {} finally { setAckPreviewLoading(false); }
                  }} className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
                    <Users size={11}/>{ackPreviewLoading ? 'Loading...' : 'Preview Recipients'}
                  </button>
                </>
              ) : (
                /* By Specific Users mode */
                <>
                  <div>
                    <label className="block text-xs font-medium mb-2" style={{color:'#475569'}}>Search and select users</label>
                    <input
                      type="text" value={userSearch} onChange={e => setUserSearch(e.target.value)}
                      placeholder="Search by name or username..."
                      className="input-dark mb-2"/>
                    <div className="rounded-xl overflow-hidden" style={{border:'1px solid rgba(255,255,255,0.08)',maxHeight:220,overflowY:'auto'}}>
                      {allUsers.length === 0
                        ? <div className="p-3 text-xs" style={{color:'#475569'}}>Loading users...</div>
                        : allUsers.filter(u => !userSearch || u.full_name?.toLowerCase().includes(userSearch.toLowerCase()) || u.username?.toLowerCase().includes(userSearch.toLowerCase()))
                            .map(u => {
                              const sel = ackUsers.includes(u.id);
                              return (
                                <button key={u.id} type="button"
                                  onClick={() => { setAckUsers(prev => sel ? prev.filter(x => x !== u.id) : [...prev, u.id]); setAckPreview([]); }}
                                  style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'8px 12px', background: sel ? 'rgba(99,102,241,0.15)' : 'transparent', border:'none', borderBottom:'1px solid rgba(255,255,255,0.04)', cursor:'pointer', textAlign:'left' }}>
                                  <span style={{ width:14, height:14, border:`1px solid ${sel?'#818cf8':'#334155'}`, borderRadius:3, background: sel?'rgba(99,102,241,0.3)':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                    {sel && <span style={{color:'#818cf8',fontSize:9,fontWeight:'bold'}}>✓</span>}
                                  </span>
                                  <span style={{flex:1}}>
                                    <span className="text-xs font-medium" style={{color:'#e2e8f0'}}>{u.full_name}</span>
                                    <span className="text-xs ml-2" style={{color:'#475569'}}>@{u.username}</span>
                                  </span>
                                  <span className="text-xs px-1.5 py-0.5 rounded" style={{background:'rgba(255,255,255,0.05)',color:'#64748b',fontSize:10}}>{u.role?.replace(/_/g,' ')}</span>
                                </button>
                              );
                            })
                      }
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs" style={{color: ackUsers.length ? '#818cf8' : '#475569'}}>{ackUsers.length} user{ackUsers.length !== 1 ? 's' : ''} selected</span>
                      {ackUsers.length > 0 && <button type="button" onClick={() => setAckUsers([])} className="text-xs" style={{color:'#ef4444',background:'none',border:'none',cursor:'pointer'}}>Clear all</button>}
                    </div>
                    {ackUsers.length === 0 && <div className="mt-1 text-xs" style={{color:'#f59e0b'}}>Select at least one user.</div>}
                  </div>
                </>
              )}

              {ackPreview.length > 0 && (
                <div className="p-3 rounded-xl text-xs" style={{background:'rgba(59,130,246,0.06)',border:'1px solid rgba(59,130,246,0.2)'}}>
                  <span style={{color:'#60a5fa',fontWeight:600}}>{ackPreview.length} users</span>
                  <span style={{color:'#475569'}}> · {ackPreview.slice(0,5).map(u=>u.full_name).join(', ')}{ackPreview.length>5?` +${ackPreview.length-5} more`:''}</span>
                </div>
              )}

              {/* Policies to be sent */}
              <div className="rounded-xl p-3 text-xs" style={{background:'rgba(99,102,241,0.06)',border:'1px solid rgba(99,102,241,0.15)'}}>
                <div className="font-semibold mb-2" style={{color:'#818cf8'}}>Policies included in this request:</div>
                {policies.length === 0 ? (
                  <div style={{color:'#f59e0b'}}>⚠ No policy documents uploaded yet. Upload policies first.</div>
                ) : (
                  <ul className="space-y-1" style={{color:'#94a3b8'}}>
                    {policies.map(p => <li key={p.id}>📄 {p.name}</li>)}
                  </ul>
                )}
              </div>

              {ackError && <div className="p-3 rounded-xl text-xs" style={{background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.25)',color:'#f87171'}}>{ackError}</div>}
              {ackResult && (
                <div className="p-3 rounded-xl text-xs" style={{background:'rgba(16,185,129,0.08)',border:'1px solid rgba(16,185,129,0.25)',color:'#34d399'}}>
                  ✓ Done — <strong>{ackResult.emailsSent}</strong> emails sent, <strong>{ackResult.created}</strong> new acknowledgement requests created.
                  {ackResult.skipped > 0 && <span style={{color:'#475569'}}> ({ackResult.skipped} skipped — no email on file)</span>}
                </div>
              )}

              <button
                type="button"
                disabled={ackSending || policies.length === 0 || (ackMode === 'role' && ackRoles.length === 0) || (ackMode === 'users' && ackUsers.length === 0)}
                onClick={handleSendAck}
                className="btn-primary w-full flex items-center justify-center gap-2"
                style={{opacity: (ackSending || policies.length === 0 || (ackMode === 'role' && ackRoles.length === 0) || (ackMode === 'users' && ackUsers.length === 0)) ? 0.6 : 1}}>
                <Send size={13}/>{ackSending ? 'Sending...' : 'Send Acknowledgement Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── POLICY ACKNOWLEDGEMENT RECORDS TAB ──────────────── */}
      {tab === 'acknowledgements' && (() => {
        const pendingRecs = ackRecords.filter(r => r.status === 'pending');
        const acknowledgedRecs = ackRecords.filter(r => r.status === 'acknowledged');
        const pendingUserIds = [...new Set(pendingRecs.filter(r => r.system_user_id).map(r => r.system_user_id))];

        const AckTable = ({ rows, showResend }) => (
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                {['User','Company / Role','Policy','Requested', showResend ? 'Acknowledged On' : 'Acknowledged On', showResend && isAdmin ? 'Action' : ''].filter(Boolean).map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const uid = r.system_user_id;
                const resendState = resendResults[uid];
                const isSending = resendingId === uid;
                return (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.05)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '9px 14px', color: '#e2e8f0', fontWeight: 500 }}>{r.display_name || '—'}</td>
                    <td style={{ padding: '9px 14px', color: '#818cf8', fontFamily: 'monospace', fontSize: 11 }}>{r.display_company !== '—' ? r.display_company : (r.display_role || '—')}</td>
                    <td style={{ padding: '9px 14px', color: '#94a3b8' }}>{r.policy_name || r.policy?.name || '—'}</td>
                    <td style={{ padding: '9px 14px', color: '#475569', whiteSpace: 'nowrap' }}>
                      {r.requested_at ? new Date(r.requested_at).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td style={{ padding: '9px 14px', color: '#34d399', whiteSpace: 'nowrap' }}>
                      {r.acknowledged_at ? (
                        <div>
                          <div>{new Date(r.acknowledged_at).toLocaleDateString('en-GB')}</div>
                          <div style={{color:'#475569',fontSize:10}}>{new Date(r.acknowledged_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}</div>
                        </div>
                      ) : <span style={{ color: '#1e3a5f' }}>—</span>}
                    </td>
                    {showResend && isAdmin && (
                      <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}>
                        {uid ? (
                          resendState === 'ok' ? (
                            <span className="text-xs flex items-center gap-1" style={{ color: '#34d399' }}><CheckCircle size={11}/>Sent</span>
                          ) : resendState === 'err' ? (
                            <button onClick={() => handleResend(uid)} className="text-xs" style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>Failed — Retry</button>
                          ) : (
                            <button onClick={() => handleResend(uid)} disabled={isSending}
                              className="btn-ghost text-xs px-2.5 py-1 flex items-center gap-1"
                              style={{ color: '#f59e0b', borderColor: 'rgba(245,158,11,0.3)', opacity: isSending ? 0.6 : 1 }}>
                              <RefreshCw size={10} className={isSending ? 'animate-spin' : ''}/>
                              {isSending ? 'Sending…' : 'Resend'}
                            </button>
                          )
                        ) : <span style={{ color: '#1e3a5f', fontSize: 11 }}>—</span>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        );

        return (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex gap-2 flex-wrap items-center justify-between">
              <div className="flex gap-2 flex-wrap">
                <select value={ackFilterPolicy} onChange={e => setAckFilterPolicy(e.target.value)} className="input-dark text-xs" style={{width:'auto',minWidth:200}}>
                  <option value="">All Policies</option>
                  {policies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <SearchableSelect value={ackFilterCompany} onChange={setAckFilterCompany}
                  style={{minWidth:180}}
                  options={companies.map(c => ({ value: c.id, label: c.name, hint: c.code }))}
                  placeholder="All Companies"/>
                <button onClick={loadAckRecords} className="btn-primary text-xs px-3 py-1.5">Apply Filter</button>
              </div>
              {pendingUserIds.length > 0 && isAdmin && (
                <button onClick={handleResendAll} disabled={resendAllLoading}
                  className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5"
                  style={{ color: '#f59e0b', borderColor: 'rgba(245,158,11,0.3)', opacity: resendAllLoading ? 0.6 : 1 }}>
                  <RefreshCw size={11} className={resendAllLoading ? 'animate-spin' : ''}/>
                  {resendAllLoading ? 'Resending...' : `Resend All Pending (${pendingUserIds.length})`}
                </button>
              )}
            </div>

            {/* SECTION 1 — Pending */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle size={13} style={{ color: '#f59e0b' }}/>
                <span className="text-xs font-semibold" style={{ color: '#f59e0b' }}>
                  Pending Acknowledgement — {pendingUserIds.length} user{pendingUserIds.length !== 1 ? 's' : ''} not yet acknowledged
                </span>
              </div>
              <div className="glass rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(245,158,11,0.2)' }}>
                {pendingRecs.length === 0 ? (
                  <div className="flex items-center justify-center h-16 text-xs" style={{ color: '#34d399' }}>
                    ✓ All users have acknowledged their IT policies.
                  </div>
                ) : (
                  <AckTable rows={pendingRecs} showResend={true} />
                )}
              </div>
            </div>

            {/* SECTION 2 — Acknowledged */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={13} style={{ color: '#34d399' }}/>
                <span className="text-xs font-semibold" style={{ color: '#34d399' }}>
                  Acknowledged — {acknowledgedRecs.length} record{acknowledgedRecs.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="glass rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(16,185,129,0.15)' }}>
                {acknowledgedRecs.length === 0 ? (
                  <div className="flex items-center justify-center h-16 text-xs" style={{ color: '#1e3a5f' }}>
                    No acknowledgements recorded yet.
                  </div>
                ) : (
                  <AckTable rows={acknowledgedRecs} showResend={false} />
                )}
              </div>
            </div>

            {/* SECTION 3 — Never requested (previously invisible in this tab) */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle size={13} style={{ color: '#94a3b8' }}/>
                <span className="text-xs font-semibold" style={{ color: '#94a3b8' }}>
                  Not Yet Requested — {neverRequested.length} active user{neverRequested.length !== 1 ? 's' : ''} never sent a policy acknowledgement
                </span>
              </div>
              <div className="glass rounded-2xl p-4" style={{ border: '1px solid rgba(148,163,184,0.15)' }}>
                {neverRequested.length === 0 ? (
                  <div className="flex items-center justify-center h-10 text-xs" style={{ color: '#34d399' }}>
                    ✓ Every active user has been sent at least one acknowledgement request.
                  </div>
                ) : (
                  <>
                    <div className="text-xs mb-3" style={{ color: '#475569' }}>
                      These users have no acknowledgement record at all. Use the <strong>Send for Acknowledgement</strong> tab to request them.
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {neverRequested.map(u => (
                        <span key={u.id} className="text-xs px-2 py-1 rounded-lg"
                          style={{ background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.2)', color: '#94a3b8' }}>
                          {u.full_name} <span style={{ color: '#475569' }}>({u.username} · {(u.role || '').replace(/_/g, ' ')})</span>
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── HANDOVER ACCEPTANCES TAB ─────────────────────────── */}
      {tab === 'handovers' && (
        <div className="glass glow-border rounded-2xl overflow-hidden">
          {acceptances.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-sm" style={{ color: '#1e3a5f' }}>
              No handover acceptance records yet.
            </div>
          ) : (
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  {['Asset','Employee','Status','Accepted On','IP Address'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {acceptances.map((a, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.05)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '9px 14px', fontFamily: 'monospace', color: '#60a5fa' }}>{a.asset?.asset_number || '—'}</td>
                    <td style={{ padding: '9px 14px', color: '#e2e8f0', fontWeight: 500 }}>{a.employee?.full_name || '—'}</td>
                    <td style={{ padding: '9px 14px' }}>
                      {a.accepted_at
                        ? <span className="flex items-center gap-1 text-xs" style={{ color: '#34d399' }}><CheckCircle size={12}/>Accepted</span>
                        : <span className="flex items-center gap-1 text-xs" style={{ color: '#f59e0b' }}><AlertCircle size={12}/>Pending</span>
                      }
                    </td>
                    <td style={{ padding: '9px 14px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                      {a.accepted_at ? new Date(a.accepted_at).toLocaleString() : <span style={{ color: '#1e3a5f' }}>—</span>}
                    </td>
                    <td style={{ padding: '9px 14px', color: '#334155', fontFamily: 'monospace', fontSize: 11 }}>{a.acceptance_ip || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
